// Shopify Preictive Search - https://shopify.dev/docs/themes/navigation-search/search/predictive-search
// Classic script (copied verbatim, not bundled): the wrapper keeps every
// binding below out of the page's shared global scope, where a same-named
// declaration in any other script would kill one of the two at parse time.
/* eslint indent: ["error", 4, { "SwitchCase": 1, "outerIIFEBody": 0 }] -- IIFE body intentionally not re-indented, keeps the wrap a reviewable two-line diff */
(function () {
const SEARCH_TYPE = 'predictive';

// The GA4 property for worksharptools.com ("Work Sharp Outdoor - GA4").
// send_to must pin every event here: the page's Google tag also serves Ads
// and Merchant Center destinations that must not receive these events.
const GA4_MEASUREMENT_ID = 'G-6ZR2BEMR9Z';

class PredictiveSearch extends HTMLElement {
    constructor() {
        super();

        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('#predictive-search');
        this.searchOverlay = document.querySelector('#search-overlay');
        this.resourcesType = this.input.dataset.resourcesType;
        this.mobileDisable = this.input.dataset.mobileDisable;
        this.lastTrackedTerm = '';
        this.lastTermReportable = false;

        // Delegated so it survives the innerHTML swap in getSearchResults().
        // The container id is prefixed to 'mobile--predictive-search' when the
        // Disable Predictive Search on Mobile theme setting is on, so the
        // lookup above returns null there. Guarded so a throwing constructor
        // never blocks this element from upgrading.
        if (this.predictiveSearchResults) {
            this.predictiveSearchResults.addEventListener('click', (event) => {
                this.trackResultClick(event);
            });
        }

        // The header search form's own close paths (snippets/search-form/
        // search.js) clear the input programmatically, which fires no 'input'
        // event, so close() never runs there and the dedupe below would
        // swallow the shopper's next identical search. Re-opening the form
        // always focuses the input, so focus marks a fresh search session.
        // Guarded on an empty box: those close paths always clear the value
        // first, while a mid-session refocus (tab out and back, quick-add in
        // the dropdown) still has text and must keep its click attribution.
        this.input.addEventListener('focus', () => {
            if (this.input.value.trim() === '') {
                this.lastTrackedTerm = '';
                this.lastTermReportable = false;
            }
        });

        if (this.mobileDisable == 'false') {
            this.input.addEventListener('input', this.debounce((event) => {
                this.onChange(event);
            }, 300).bind(this));
        }
    }

    onChange() {
        const searchTerm = this.input.value.trim();

        if (!searchTerm.length) {
            this.close();
            return;
        }

        this.getSearchResults(searchTerm, this.resourcesType);
    }

    getSearchResults(searchTerm, resourcesType) {
        fetch(`/search/suggest?q=${searchTerm}&resources[options][fields]=body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor,body,author&resources[type]=${resourcesType}&resources[options][unavailable_products]=last&resources[limit]=10&section_id=predictive-search`)
            .then((response) => {
                if (!response.ok) {
                    var error = new Error(response.status);
                    this.close();
                    throw error;
                }

                return response.text();
            })
            .then((text) => {
                const resultsSection = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');

                this.trackSearch(searchTerm, resultsSection);

                this.predictiveSearchResults.innerHTML = resultsSection.innerHTML;
                this.open();
            })
            .catch((error) => {
                this.close();
                throw error;
            });
    }

    // Native predictive search reports nothing to analytics, and neither the
    // Shopify search reports nor GA4 enhanced measurement can see the dropdown
    // because it never loads /search?q=. This is the only place that query
    // stream is observable.
    trackSearch(searchTerm, resultsSection) {
        try {
            // The section can be missing from the fetched markup. Bail before
            // any state changes so the term is not burned by the dedupe.
            if (!resultsSection) {
                return;
            }

            // Shoppers paste emails and order numbers into search boxes. The
            // cap bounds what reaches GA4 (which truncates params at 100
            // chars anyway); stream-level email redaction is the second layer.
            const raw = searchTerm.toLowerCase();
            const term = raw.slice(0, 100);

            // A trailing space re-fires the debounced handler with an identical
            // trimmed term. Suppress only that immediate repeat. close() and
            // refocus clear this, so a shopper genuinely returning to an
            // earlier term still counts - a repeated search is signal, not noise.
            if (term === this.lastTrackedTerm) {
                return;
            }

            // Counts rendered <li> rather than the Liquid .size, so products
            // tagged 'hide-from-search' are excluded the same way the shopper
            // sees them excluded.
            const productCount = resultsSection.querySelectorAll('li.predictive-search__list-item').length;
            const queryCount = resultsSection.querySelectorAll('.predictive-search__results-list--queries li').length;

            // Gate on the uncapped text so a digit run straddling the cap
            // cannot slip through truncated. Cached so the click event uses
            // the same verdict instead of re-judging the capped term.
            const reportable = this.isReportable(raw);

            if (reportable) {
                this.sendEvent('search_suggestion_shown', {
                    search_term: term,
                    results_count: productCount,
                    no_results: productCount === 0 && queryCount === 0,
                });
            }

            // Set only after everything above that can throw, including the
            // send itself, so a failure never burns the term for the session.
            // Non-reportable terms still land here: their dropdown is real,
            // so clicks in it need the dedupe and attribution state.
            this.lastTrackedTerm = term;
            this.lastTermReportable = reportable;
        } catch (error) {
            // Measurement must never take the search bar down with it.
        }
    }

    // results_count alone can't tell "found nothing" from "found the wrong
    // things" - the RMX case returns products, just not the sharpener. Pairing
    // searches with what got clicked is what separates the two.
    trackResultClick(event) {
        try {
            const target = event.target.closest('a[href], .search-results__link');

            if (!target) {
                return;
            }

            const seeAll = !target.hasAttribute('href');
            const item = target.closest('li');
            const siblings = item ? Array.from(item.parentElement.children) : [];

            this.sendEvent('predictive_search_click', {
                // Same gate verdict as the impression event: a suppressed
                // term must not leak back out through the click.
                search_term: this.lastTermReportable ? this.lastTrackedTerm : '',
                link_url: seeAll ? '' : (target.getAttribute('href') || '').slice(0, 100),
                link_text: target.textContent.trim().replace(/\s+/g, ' ').slice(0, 100),
                result_position: item ? siblings.indexOf(item) + 1 : 0,
                result_type: seeAll ? 'see_all' : this.resultTypeOf(target),
            });
        } catch (error) {
            // Never block the navigation.
        }
    }

    // Both events pass their term through this gate (2026-08-23 audit).
    // Single characters carry no analytic value, and a run of 9 or more
    // digits is phone-shaped. The longest observed part-number run is 7
    // digits, so 9 keeps SKU searches intact. Phones typed with separators
    // are accepted as uncatchable without risking that SKU traffic.
    isReportable(term) {
        return term.length > 1 && !/[0-9]{9,}/.test(term);
    }

    resultTypeOf(target) {
        const list = target.closest('ul');

        if (!list) {
            return 'unknown';
        }

        const match = list.className.match(/predictive-search__results-list--(\w+)/);

        return match ? match[1] : 'unknown';
    }

    // Sends straight to GA4 through the Google tag the Google & YouTube
    // channel app already loads in this frame. No GTM is involved while the
    // theme's gtm_container_id setting stays blank (its only storefront GTM
    // would load via layout/theme.liquid); the site's other GTM lives inside
    // a sandboxed pixel that cannot see this frame, and the main-frame
    // dataLayer is read by nothing.
    sendEvent(eventName, params) {
        // Ad blockers strip gtag. Search must keep working unmeasured, and
        // the caller's try/catch is not relied on for this common case.
        if (typeof window.gtag !== 'function') {
            return;
        }

        // Pinned fields come last so no caller can override the scoping.
        window.gtag('event', eventName, Object.assign({}, params, {
            send_to: GA4_MEASUREMENT_ID,
            search_type: SEARCH_TYPE,
        }));
    }

    open() {
        this.predictiveSearchResults.style.display = 'block';
        this.searchOverlay.style.display = 'block';

    }

    close() {
        // Ends the search session. Without this, a shopper who clears the box
        // and retypes the same term is silently dropped by the dedupe above.
        this.lastTrackedTerm = '';
        this.lastTermReportable = false;

        this.predictiveSearchResults.style.display = 'none';
        this.searchOverlay.style.display = 'none';

    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }

}

customElements.define('iam-predictive-search', PredictiveSearch);
})();

